import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Check, AlertTriangle, HelpCircle, ArrowRight, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["name", "Nome struttura"], ["property_type", "Tipo"], ["city", "Città"], ["country", "Paese"],
  ["address", "Indirizzo"], ["max_guests", "Ospiti max"], ["bedrooms", "Camere"], ["beds", "Letti"],
  ["bathrooms", "Bagni"], ["checkin_time", "Orario check-in"], ["checkout_time", "Orario check-out"],
];

const MISSING_CHECK = [
  ["checkin_time", "Orario check-in"], ["checkout_time", "Orario check-out"],
  ["address", "Indirizzo"], ["description", "Descrizione"],
];

export default function ReviewImport() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("sg_import");
    if (!raw) { navigate("/dashboard/properties/import"); return; }
    const p = JSON.parse(raw); setPayload(p); setForm(p.data || {});
  }, [navigate]);

  if (!payload) return <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
  const confidence = form._confidence || {}, missing = form._missing || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const badge = (key) => {
    if (missing.includes(key) || !form[key]) return <B tone="missing" icon={AlertTriangle} t="Mancante" />;
    const c = confidence[key];
    if (c === "needs_review" || c === "medium") return <B tone="review" icon={HelpCircle} t="Da rivedere" />;
    return <B tone="ok" icon={Check} t="Importato" />;
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...form };
      ["_confidence", "_missing", "_discovered_images", "_raw", "_error"].forEach((k) => delete clean[k]);
      const { data } = await api.post("/properties", { name: clean.name || "Struttura importata", data: clean });
      sessionStorage.removeItem("sg_import");
      const found = Object.entries(clean).filter(([k, v]) => v != null && String(v).trim() !== "").length;
      const missingList = MISSING_CHECK.filter(([k]) => !clean[k] || !String(clean[k]).trim()).map(([, l]) => l);
      track("listing_import_completed", data.property.id, { found, missing: missingList.length });
      setCreated({ property: data.property, found, missingList });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };

  if (created) {
    return (
      <div className="px-4 pt-6 pb-10 max-w-md mx-auto">
        <div className="animate-fade-up text-center pt-4">
          <div className="w-16 h-16 rounded-3xl bg-blue-600 text-white grid place-items-center mx-auto"><Check className="w-8 h-8" /></div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-5" data-testid="import-done-title">Importazione completata</h1>
          <p className="text-slate-500 mt-2">Ho trovato <span className="font-bold text-blue-600">{created.found}</span> informazioni sulla tua struttura.
            {created.missingList.length > 0 && <> Mancano ancora <span className="font-bold text-slate-900">{created.missingList.length}</span> dettagli utili per completare la Guest Guide.</>}</p>
        </div>
        {created.missingList.length > 0 && (
          <div className="mt-5 rounded-3xl bg-white border border-slate-100 p-5">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Da completare</p>
            <div className="mt-2 flex flex-wrap gap-2">{created.missingList.map((l) => <span key={l} className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full">{l}</span>)}</div>
          </div>
        )}
        <button onClick={() => navigate(`/dashboard/properties/${created.property.id}/wizard`)} data-testid="import-complete-ai" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-2xl py-3.5"><Sparkles className="w-4 h-4" /> Completa con l'AI</button>
        <button onClick={() => navigate(`/dashboard/properties/${created.property.id}/edit`)} data-testid="import-go-guide" className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-semibold rounded-2xl py-3.5">Vai direttamente alla guida</button>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-10 max-w-md mx-auto">
      <button onClick={() => navigate("/dashboard/properties/import")} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="w-4 h-4" /> Indietro</button>
      <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Rivedi i dati importati</h1>
      <p className="mt-1 text-slate-500">Controlla le informazioni, completa ciò che manca e crea la struttura. Niente viene pubblicato automaticamente.</p>

      <div className="mt-5 space-y-3" data-testid="review-fields">
        {FIELDS.map(([key, label]) => (
          <div key={key} className="rounded-xl border border-slate-100 bg-white p-3.5">
            <div className="flex items-center justify-between mb-1.5"><label className="text-sm font-semibold text-slate-700">{label}</label>{badge(key)}</div>
            <input value={form[key] ?? ""} onChange={(e) => set(key, e.target.value)} data-testid={`review-${key}`} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          </div>
        ))}
        <div className="rounded-xl border border-slate-100 bg-white p-3.5">
          <div className="flex items-center justify-between mb-1.5"><label className="text-sm font-semibold text-slate-700">Descrizione</label>{badge("description")}</div>
          <textarea value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} rows={4} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 resize-none" />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="review-save" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-500 text-white font-semibold rounded-xl py-3.5 disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Crea struttura e continua <ArrowRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

const B = ({ tone, icon: Icon, t }) => {
  const cls = { ok: "bg-emerald-50 text-emerald-600", review: "bg-amber-50 text-amber-600", missing: "bg-slate-100 text-slate-400" }[tone];
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}><Icon className="w-3 h-3" />{t}</span>;
};
